<script setup>
/* Imports */
import {
  computed,
  nextTick,
} from 'vue';

/* Components */
import Price from './Price.vue';

/* Store */
import { useGlobalStore } from '../store/global.js';
import { usePricingStore } from '../store/pricing.js';

/* Helpers */
import { getProductPageOrigin } from '../helpers/getProductPageOrigin.js';

/* Init store */
const globalStore = useGlobalStore();
const pricingStore = usePricingStore();

/* Props */
const props = defineProps({
  card: {
    type: Object,
    default: () => {},
  },
});

/* Emits */
const emit = defineEmits(['cardClick']);

/* Product URL */
const productPageOrigin = getProductPageOrigin(window?.location?.origin);
const productUrl = computed(() => props.card?.productUrl?.replace('https://www.garmin.com', productPageOrigin));

/* Srcset */
const getSrcSet = computed(() => {
  const { card } = props;
  const { smallImage, mediumImage } = card?.media || {};

  if (smallImage) {
    if (mediumImage) {
      return `${smallImage}, ${mediumImage} 2x`;
    }
    return `${smallImage}`;
  }

  return globalStore.getTranslations?.ITFE_PRODUCT_COMPARE_PLACEHOLDER_IMAGE;
});

/* Remove product */
const removeProduct = async (pid) => {
  // Check if this is the last product before removing it
  const isLastProduct = globalStore.getProductData?.products?.length === 1;

  // Remove the product from the store
  globalStore.removeProduct(pid);

  // Scroll if last product — avoid lonely white screen
  if (isLastProduct) {
    await nextTick();
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }
};

const handleCardClick = () => emit('cardClick');
</script>

<template>
  <div
    class="product-compare-card"
    data-testid="product-compare-card"
  >
    <div class="product-compare-card__header">
      <a
        class="product-compare-card__image-link"
        :href="productUrl"
        data-testid="product-compare-card-image-link"
        :aria-label="props.card?.displayName"
        tabindex="0"
        @click="handleCardClick()"
        @keydown.enter="handleCardClick()"
        @keydown.space="handleCardClick()"
      >
        <img
          class="product-compare-card__image"
          :srcset="getSrcSet"
          alt=""
          data-testid="product-compare-card-image"
        >
        <g-copy
          class="product-compare-card__sticky-title"
          mb="none"
          data-testid="product-compare-card-sticky-title"
        >
          {{ props.card?.displayName }}
        </g-copy>
      </a>
      <button
        class="product-compare-card__close-button"
        tabindex="0"
        type="button"
        data-testid="product-compare-card-close-button"
        :title="globalStore.getTranslations?.ITFE_PRODUCT_COMPARE_CLOSE_CARD_BUTTON"
        :aria-label="globalStore.getTranslations?.ITFE_PRODUCT_COMPARE_CLOSE_CARD_BUTTON"
        @click="removeProduct(props.card?.pid)"
        @keydown.enter="removeProduct(props.card?.pid)"
        @keydown.space="removeProduct(props.card?.pid)"
      >
        &times;
      </button>
    </div>

    <div class="product-compare-card__body">
      <div class="product-compare-card__content">
        <g-copy
          class="product-compare-card__title"
          mb="md"
          :dataTestId="{
            host: 'product-compare-card-title'
          }"
        >
          <p>{{ props.card?.displayName }}</p>
        </g-copy>

        <Price
          class="product-compare-card__price"
          :price-is-loading="pricingStore.getIsPriceLoading(props.card?.pid)"
          :pricing-data="pricingStore.getProductPricingByPid(props.card?.pid)"
          data-testid="product-compare-card-price"
        />
      </div>

      <g-button
        v-if="props.card?.sellable"
        theme="candy-blue"
        size="medium"
        :href="productUrl"
        class="product-compare-card__shop-button"
        :dataTestId="{
          host: 'product-compare-card-shop-button-host',
          tag: 'product-compare-card-shop-button-tag',
        }"
        @click="handleCardClick()"
      >
        {{ globalStore.getTranslations?.ITFE_PRODUCT_COMPARE_SHOP_BUTTON_MESSAGE }}
      </g-button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.product-compare-card {
  display: flex;
  flex-direction: column;
  flex: 0 0 100%;

  &__header {
    position: relative;
  }

  &__image-link {
    display: flex;
    flex-basis: 0;
    flex-grow: 1;
    align-items: center;
    justify-content: center;
    height: 100%;
    position: relative;
    padding: 1.125rem 0.75rem;
    text-decoration: none;
    @include make-drop-shadow();

    &:hover {
      color: initial;
    }
  }

  &__image {
    width: 7.8125rem;   // 125px
    height: 7.8125rem;  // 125px
    object-fit: contain;

    @include breakpoint('sm') {
      width: 9.375rem;  // 150px
      height: 9.375rem; // 150px
    }
  }

  &__sticky-title {
    display: none;
  }

  &__close-button {
    position: absolute;
    top: 0;
    right: 0;
    cursor: pointer;
    background: none;
    border: none;
    color: $color-black; // iOS
    font-size: 1.75rem;
    padding: 0 0.5rem; // extend x-able area
  }

  &__body {
    padding-top: 1rem;
    display: flex;
    flex: 1 1;
    flex-direction: column;
  }

  &__content {
    flex-grow: 1;
  }

  &__price {
    margin-bottom: 2rem;
  }

  /* Sticky menu variant */
  .sticky-menu.active & {

    &__body {
      display: none;
    }

    &__image-link {
      justify-content: flex-start;

      @media (max-width: $compare-screen-small) {
        padding: 1.35rem 0.75rem 0.9rem;
      }
    }

    &__image {
      width: 3.75rem; // 60px
      height: 3.75rem; // 60px
    }

    &__sticky-title {
      font-size: $font-size-small;
      /* Multi-line truncate */
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      overflow: hidden;
      text-overflow: ellipsis;
      word-break: break-word;
    }

    &__close-button {
      font-size: 1.5rem;
    }
  }
}
</style>
